import { Inject, Injectable } from '@angular/core';
import { doc, docData, DocumentReference, getFirestore } from '@angular/fire/firestore';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { COMMONS_ENV, HttpService, NetworkService } from '@damofire/data-angular-commons';
import { logger, HttpUtils, ID_FIELD_OPTIONS, MEDIAS_COLLECTION, Query, SearchResult, WW_REPAIRS_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService } from '@damofire/data-security';
import { firstValueFrom } from 'rxjs';
import { TypedDesQuery } from '../des.dto';
import { IMAGE_NOT_AVAILABLE, IMAGE_NOT_FOUND } from '../issue/issue.dto';
import { FirebaseObservableRepairService } from './firebase-repair-observable.service';
import { Repair, RepairMedia } from './repair.dto';
import { RepairService } from './repair.service';

@Injectable()
export class FirebaseRepairService extends RepairService {
  async getCsv(query: Query): Promise<string> {
    throw new Error('Method not implemented.');
  }

  async buildCustomItemList(id: string, urlSearchString: string): Promise<string[]> {
    throw new Error('Method not implemented.');
  }

  constructor(
    private readonly dtoSigner: AuthDtoSignerService,
    private readonly http: HttpService,
    protected readonly networkService: NetworkService,
    protected readonly storage: Storage,
    @Inject(COMMONS_ENV) protected readonly environment: any,
    @Inject(FirebaseObservableRepairService) private readonly observableRepairService: FirebaseObservableRepairService,
  ) {
    super(networkService, storage);
  }

  async getImage(media: RepairMedia, type: 'thumb' | 'web' | 'original'): Promise<string> {
    let url = IMAGE_NOT_AVAILABLE;

    if (!media.web) {
      return url;
    }

    logger.debug('Getting image from online source', { path: media[type], category: 'repair' });
    const file = ref(this.storage, media[type]);

    try {
      url = await getDownloadURL(file);
    } catch (err) {
      logger.debug('Image does not exist, falling back to original size', { path: media[type], category: 'repair' });
      const fallback = ref(this.storage, media.original);
      try {
        url = await getDownloadURL(fallback);
      } catch (err2) {
        logger.warn('Image not found', { category: 'repair', error: err2 });
        url = IMAGE_NOT_FOUND;
      }
    }

    return url;
  }

  async updateMedia(media: RepairMedia): Promise<void> {
    await this.dtoSigner.update(media, `${WW_REPAIRS_COLLECTION}/${media.repair?._id}/${MEDIAS_COLLECTION}/${media._id}`);
  }

  async upsert(repair: Partial<Repair>, repairId?: string): Promise<Repair> {
    const sanitizedRepair: Partial<Repair> = {
      ...repair,
      issues: repair.issues?.map((issue) => this.sanitizeIssue(issue)),
    };

    if (repairId) {
      return HttpUtils.put<Repair>(this.environment.appConfig.fns, `ww-repairs/${repairId}`, sanitizedRepair);
    } else {
      return HttpUtils.post<Repair>(this.environment.appConfig.fns, 'ww-repairs', sanitizedRepair);
    }
  }

  private sanitizeIssue(issue: any) {
    const { status, ...rest } = issue;
    return status?._id === -1 ? rest : issue;
  }

  async createRepairMedia(repairId: string, filename: string, bytes: string): Promise<void> {
    const data = bytes.replace(/data:image\/[a-zA-Z]+;base64,/, '');
    return HttpUtils.post<void>(this.environment.appConfig.fns, 'ww-repairs/media/create', { filename, data, repairId });
  }

  async deleteMedia(media: RepairMedia): Promise<void> {
    await HttpUtils.delete<void>(this.environment.appConfig.fns, `ww-repairs/media/${media._id}`);
  }

  async deleteIssue(issueId: string, repairId: string): Promise<void> {
    await HttpUtils.delete<void>(this.environment.appConfig.fns, `ww-repairs/${repairId}/issues/${issueId}`);
  }

  async get(repairId: string): Promise<Repair> {
    return firstValueFrom(docData<Repair>(doc(getFirestore(), `${WW_REPAIRS_COLLECTION}/${repairId}`) as DocumentReference<Repair>, ID_FIELD_OPTIONS));
  }

  async search(id: string, sQuery: TypedDesQuery): Promise<SearchResult<Repair>> {
    return firstValueFrom(this.observableRepairService.search(id, sQuery));
  }

  async delete(id: string): Promise<void> {
    await HttpUtils.delete<void>(this.environment.appConfig.fns, `ww-repairs/${id}`);
  }

  async deleteBatch(ids: string[]): Promise<void> {
    await HttpUtils.post<void>(this.environment.appConfig.fns, `ww-repairs/delete-batch`, { ids });
  }

  async getRepairsByUpright(divisionId: string, assetId: number | string): Promise<Repair[]> {
    const { result } = await firstValueFrom(this.observableRepairService.search(divisionId, { filters: { 'upright._id': assetId.toString() } }));
    return result;
  }
}
